// Exercices PARAMÉTRÉS : un exercice déclare des paramètres, et tous ses textes sont des gabarits.
//
//   "params": {
//     "vars":     { "a": [1, 9], "b": { "range": [2, 9], "exclude": [5] }, "s": [-5, -1, 1, 2] },  // intervalle ou liste
//     "where":    ["gcd(a, b) == 1", "b != d"],          // contraintes (toutes vraies)
//     "let":      { "m": "b * d" },                        // valeurs dérivées, dans l'ordre
//     "variants": [ { …surcharges de l'exercice… }, … ],  // facultatif : une variante tirée (fusion profonde)
//     "default":  { "a": 2, "b": 3, …, "variant": 0 }      // l'instance de référence (tests, démo)
//   }
//
// Gabarits dans TOUTES les chaînes de l'exercice : [[expr]] et ses formes
//   [[e]]      valeur (« −4 » avec le vrai signe moins)      [[e:c]]   coefficient : 1 → « », −1 → « − »
//   [[+e]]     terme signé : « + 4 » / « − 4 »                [[+e:c]]  terme signé en coefficient : « + », « − 3 »
//   fonctions : gcd, lcm, abs, min, max, binom(n, k), pow(a, b) ; et, rendant du texte : dec(p, q) (décimal fini, sinon fraction), frac(p, q) (fraction réduite « −2/15 » ou entier),
//   lin(p, q[, "x"]) (« 3x − 4 », sans terme nul), quad(a, b, c[, "x"]) (« 2x² − 8x + 4 »).
// Langage FERMÉ (entiers, + − * / %, comparaisons, && || !, ces fonctions) : aucun code exécuté.
// instantiate(exercise, seed) : seed absent → instance « default » (identique à l'exercice de référence).

const MINUS = '−';

// ── Expressions ───────────────────────────────────────────────────────────
function lex(src) {
  const out = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9]/.test(c)) {
      const m = /^[0-9]+/.exec(src.slice(i));
      out.push({ k: 'n', v: +m[0] });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
      out.push({ k: 'id', v: m[0] });
      i += m[0].length;
      continue;
    }
    if (c === '"' || c === "'") {
      const j = src.indexOf(c, i + 1);
      if (j < 0) throw new Error('texte non fermé');
      out.push({ k: 's', v: src.slice(i + 1, j) });
      i = j + 1;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['==', '!=', '<=', '>=', '&&', '||'].includes(two)) {
      out.push({ k: 'op', v: two });
      i += 2;
      continue;
    }
    if ('+-−*/%()<>!,'.includes(c)) {
      out.push({ k: 'op', v: c === '−' ? '-' : c });
      i++;
      continue;
    }
    throw new Error(`caractère inattendu « ${c} »`);
  }
  return out;
}
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
};
const FUNCS = {
  gcd: (a, b) => gcd(a, b),
  lcm: (a, b) => Math.abs(a * b) / (gcd(a, b) || 1),
  abs: Math.abs,
  min: Math.min,
  max: Math.max,
  frac: (p, q) => {
    if (q === 0) throw new Error('frac : dénominateur nul');
    if (q < 0) {
      p = -p;
      q = -q;
    }
    const g = gcd(p, q) || 1;
    p /= g;
    q /= g;
    return { text: q === 1 ? fmt(p) : `${p < 0 ? MINUS : ''}${Math.abs(p)}/${q}` };
  },
  binom: (n, k) => {
    let c = 1;
    for (let i = 0; i < k; i++) c = (c * (n - i)) / (i + 1);
    return Math.round(c);
  },
  pow: (a, b) => a ** b,
  // écriture décimale de p/q (« 0,2646 ») si elle est finie, sinon la fraction réduite
  dec: (p, q) => {
    if (q === 0) throw new Error('dec : dénominateur nul');
    let d = Math.abs(q) / (gcd(p, q) || 1),
      k = 0;
    while (d % 2 === 0) {
      d /= 2;
      k++;
    }
    while (d % 5 === 0) {
      d /= 5;
      k++;
    }
    if (d !== 1) return FUNCS.frac(p, q);
    const neg = p * q < 0,
      v = Math.abs(p) / Math.abs(q);
    const t = v
      .toFixed(Math.min(k + 1, 12))
      .replace(/\.?0+$/, '')
      .replace('.', ',');
    return { text: (neg ? MINUS : '') + t };
  },
  lin: (p, q, x = 'x') => ({
    text: poly([
      [p, x],
      [q, ''],
    ]),
  }),
  quad: (a, b, c, x = 'x') => ({
    text: poly([
      [a, `${x}²`],
      [b, x],
      [c, ''],
    ]),
  }),
};
// somme de monômes (coefficient, partie littérale) : termes nuls omis, coefficients 1 / −1 cachés
function poly(terms) {
  let out = '';
  for (const [c, lit] of terms) {
    if (c === 0) continue;
    const abs = Math.abs(c);
    const body = lit && abs === 1 ? lit : `${abs}${lit}`;
    if (!out) out = (c < 0 ? MINUS : '') + body;
    else out += (c < 0 ? ` ${MINUS} ` : ' + ') + body;
  }
  return out || '0';
}
function evalExpr(src, env) {
  const t = lex(src);
  let i = 0;
  const peek = () => t[i],
    next = () => t[i++];
  const isOp = (v) => peek()?.k === 'op' && peek().v === v;
  const num = (x) => {
    if (typeof x !== 'number') throw new Error('un nombre est attendu');
    return x;
  };
  function primary() {
    const k = next();
    if (!k) throw new Error('expression incomplète');
    if (k.k === 'n') return k.v;
    if (k.k === 's') return k.v;
    if (k.k === 'id') {
      if (isOp('(')) {
        next();
        const args = [];
        if (!isOp(')')) {
          args.push(or());
          while (isOp(',')) {
            next();
            args.push(or());
          }
        }
        if (!isOp(')')) throw new Error('parenthèse manquante');
        next();
        const f = FUNCS[k.v];
        if (!f) throw new Error(`fonction inconnue « ${k.v} »`);
        return f(...args);
      }
      if (!(k.v in env)) throw new Error(`paramètre inconnu « ${k.v} »`);
      return env[k.v];
    }
    if (k.v === '(') {
      const v = or();
      if (!isOp(')')) throw new Error('parenthèse manquante');
      next();
      return v;
    }
    if (k.v === '-') return -num(unary());
    if (k.v === '!') return !unary();
    throw new Error(`« ${k.v} » inattendu`);
  }
  function unary() {
    return primary();
  }
  function mul() {
    let a = unary();
    while (isOp('*') || isOp('/') || isOp('%')) {
      const o = next().v,
        b = num(unary());
      num(a);
      if (o === '*') a *= b;
      else if (o === '%') a %= b;
      else {
        if (b === 0 || a % b !== 0) throw new Error(`division non entière ${a} / ${b}`);
        a /= b;
      }
    }
    return a;
  }
  function add() {
    let a = mul();
    while (isOp('+') || isOp('-')) {
      const o = next().v,
        b = num(mul());
      a = o === '+' ? num(a) + b : num(a) - b;
    }
    return a;
  }
  function cmp() {
    let a = add();
    while (['==', '!=', '<', '>', '<=', '>='].some(isOp)) {
      const o = next().v,
        b = add();
      a =
        o === '=='
          ? a === b
          : o === '!='
            ? a !== b
            : o === '<'
              ? a < b
              : o === '>'
                ? a > b
                : o === '<='
                  ? a <= b
                  : a >= b;
    }
    return a;
  }
  function and() {
    let a = cmp();
    while (isOp('&&')) {
      next();
      const b = cmp();
      a = a && b;
    }
    return a;
  }
  function or() {
    let a = and();
    while (isOp('||')) {
      next();
      const b = and();
      a = a || b;
    }
    return a;
  }
  const v = or();
  if (i < t.length)
    throw new Error(
      `fin inattendue : « ${t
        .slice(i)
        .map((x) => x.v)
        .join(' ')} »`,
    );
  return v;
}

// ── Gabarits ──────────────────────────────────────────────────────────────
const fmt = (n) => (n < 0 ? MINUS + Math.abs(n) : String(n));
const coef = (n) => (n === 1 ? '' : n === -1 ? MINUS : fmt(n));
function fill(str, env) {
  return str.replace(/\[\[(\+?)(.+?)(:c)?\]\]/g, (_, signed, expr, isCoef) => {
    const v = evalExpr(expr, env);
    if (v && typeof v === 'object') return v.text; // frac, lin, quad
    if (typeof v !== 'number') throw new Error(`[[${expr}]] ne donne pas un nombre`);
    if (signed) {
      const body = isCoef ? (Math.abs(v) === 1 ? '' : String(Math.abs(v))) : String(Math.abs(v));
      return `${v < 0 ? MINUS : '+'} ${body}`;
    }
    return isCoef ? coef(v) : fmt(v);
  });
}
function deepFill(x, env) {
  if (typeof x === 'string') return x.includes('[[') ? fill(x, env) : x;
  if (Array.isArray(x)) return x.map((y) => deepFill(y, env));
  if (x && typeof x === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(x)) {
      const f = deepFill(v, env);
      // un champ numérique écrit en gabarit (« value »: "[[x]]") redevient un nombre
      const toNum = (y) =>
        typeof y === 'string' && /^−?\d+$/.test(y) ? +y.replace(MINUS, '-') : y;
      out[k] = k === 'value' ? toNum(f) : k === 'values' && Array.isArray(f) ? f.map(toNum) : f;
    }
    return out;
  }
  return x;
}
// Fusion d'une variante : objets fusionnés, sauf `kernel` et `symbols` (et tout tableau), remplacés en entier.
const REPLACED = new Set(['kernel', 'symbols']);
function deepMerge(a, b) {
  if (Array.isArray(b) || typeof b !== 'object' || b === null) return b;
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    out[k] =
      !REPLACED.has(k) && a && typeof a[k] === 'object' && !Array.isArray(a[k])
        ? deepMerge(a[k], v)
        : v;
  }
  return out;
}

// Générateur déterministe (mulberry32) : même graine → même exercice.
function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function domain(spec) {
  if (
    Array.isArray(spec) &&
    spec.length === 2 &&
    spec.every(Number.isInteger) &&
    spec[0] <= spec[1]
  ) {
    // [min, max]
    const out = [];
    for (let v = spec[0]; v <= spec[1]; v++) out.push(v);
    return out;
  }
  if (Array.isArray(spec)) return spec;
  if (spec && Array.isArray(spec.values))
    return spec.values.filter((v) => !(spec.exclude || []).includes(v));
  if (spec && Array.isArray(spec.range))
    return domain(spec.range).filter((v) => !(spec.exclude || []).includes(v));
  throw new Error(`domaine de paramètre invalide : ${JSON.stringify(spec)}`);
}
function withLets(P, env) {
  const e = { ...env };
  for (const [k, expr] of Object.entries(P.let || {})) e[k] = evalExpr(String(expr), e);
  return e;
}
const okWhere = (P, env) => (P.where || []).every((w) => evalExpr(w, env) === true);

// instantiate(exercise, seed?) → exercice concret (sans « params »), avec `instance: { seed, values, variant }`
export function instantiate(exercise, seed = null) {
  const P = exercise.params;
  if (!P) return exercise;
  const { params, ...base } = exercise;
  let env, variant;
  if (seed == null) {
    const { variant: v = 0, ...vals } = P.default || {};
    variant = v;
    env = withLets(P, vals);
    if (!okWhere(P, env))
      throw new Error(`${exercise.id} : l’instance « default » ne respecte pas « where »`);
  } else {
    const r = rng(seed);
    const pick = (arr) => arr[Math.floor(r() * arr.length)];
    variant = P.variants?.length ? Math.floor(r() * P.variants.length) : 0;
    const doms = Object.fromEntries(Object.entries(P.vars || {}).map(([k, s]) => [k, domain(s)]));
    for (let tries = 0; tries < 5000; tries++) {
      const vals = Object.fromEntries(Object.entries(doms).map(([k, d]) => [k, pick(d)]));
      let e;
      try {
        e = withLets(P, vals);
      } catch {
        continue;
      } // division non entière… : on retire
      let ok = false;
      try {
        ok = okWhere(P, e);
      } catch {
        ok = false;
      }
      if (ok) {
        env = e;
        break;
      }
    }
    if (!env) throw new Error(`${exercise.id} : aucun tirage ne respecte « where » (5000 essais)`);
  }
  const merged = P.variants?.length ? deepMerge(base, P.variants[variant]) : base;
  const out = deepFill(merged, env);
  out.instance = {
    seed,
    variant,
    values: Object.fromEntries(Object.keys(P.vars || {}).map((k) => [k, env[k]])),
  };
  return out;
}

// Pour l'atelier et les tests : vérifie qu'un exercice paramétré se remplit (sans juger les maths).
export function checkParams(exercise, seeds = 20) {
  const errors = [];
  try {
    instantiate(exercise);
  } catch (e) {
    errors.push(`default : ${e.message}`);
  }
  for (let s = 1; s <= seeds; s++) {
    try {
      instantiate(exercise, s);
    } catch (e) {
      errors.push(`graine ${s} : ${e.message}`);
    }
  }
  return errors;
}
